import { MULTIPART_HEADERS } from '../helpers';
import {
  CreateBrandWithTemplateRequestBody,
  FooterMessageResponse,
} from '../types/api/brand.types';
import {
  ConnectProviderRequestData,
  CreateWorkingHoursRequestData,
  GetLocationsOverviewParams,
  ProviderAndConnectionStatusResponse,
  ProviderAndProviderStatusResponse,
  ProviderPauseLength,
  SalesStaticResponse,
} from '../types/api/location.types';
import { MenuEntity } from '../types/api/menu.types';
import {
  Brand,
  BrandPausedTimeDto,
  BrandPausedTimeEntity,
  BrandProviderConfigEntity,
  BrandRequestDetailsEntity,
  BrandRequestEntity,
  BrandTemplateEntity,
  BTMenuResponse,
  FoodType,
  InitiateBrandRequestDto,
} from '../types/brand.types';
import { OrdersOverviewEntity } from '../types/brandOwner';
import {
  HolidayHoursReponseV2,
  UpdateHolidayHoursLocationV2Dto,
  UpdateWorkingHoursLocationV2Dto,
  WorkingHoursReponseV2,
} from '../types/location.types';
import { OrderMigrationEntity } from '../types/order.types';
import {
  DateRangeParams,
  PaginationParams,
  Provider,
  ResponseType,
} from '../types/shared.types';
import { api } from './api';

export const createBrand = (data: FormData) =>
  api.post<ResponseType<Brand>>('/v1/brands', data, {
    headers: { 'content-type': 'multipart/form-data' },
  });

export const createBrandWithTemplate = (
  data: CreateBrandWithTemplateRequestBody
) => api.post<ResponseType<Brand>>('/v1/brands/with-template', data);

export const getBrandSalesData = (
  brandId: string | number,
  params: DateRangeParams
) =>
  api.get<ResponseType<SalesStaticResponse>>(`/v1/brands/${brandId}/sales`, {
    params,
  });

export const migrateBrandOrders = (brandId: string | number) =>
  api.post<ResponseType<null>>(`/v1/brands/${brandId}/migrate-orders`);

export const getStuckMigrations = () =>
  api.get<ResponseType<OrderMigrationEntity>>('/v1/brands/stuck-migrations');

export const connectProvider = (
  brandId: string | number,
  data: ConnectProviderRequestData
) =>
  api.post<ResponseType<string>>(
    `/v1/brands/${brandId}/provider/connect`,
    data
  );

export const transferToGrubhubPosBrand = (
  brandId: string | number,
  provider: Provider
) =>
  api.post<ResponseType<null>>(`/v1/brands/${brandId}/provider/transfer`, {
    provider,
  });

export const createProviderCredentials = (
  brandId: string | number,
  data: BrandProviderConfigEntity
) =>
  api.post<ResponseType<null>>(
    `/v1/brands/${brandId}/provider/credentials`,
    data
  );

export const getProviderConfig = (
  brandId: string | number,
  provider: Provider
) =>
  api.get<ResponseType<BrandProviderConfigEntity>>(
    `/v1/brands/${brandId}/provider/config`,
    {
      params: {
        provider,
      },
    }
  );
export const getBrandsProviderConfigs = (brandId: string | number) =>
  api.get<ResponseType<Array<BrandProviderConfigEntity>>>(
    `/v1/brands/${brandId}/provider/configs`
  );
export const getBrandsMenu = (brandId: string | number) =>
  api.get<ResponseType<Array<MenuEntity>>>(`/v1/brands/${brandId}/menu`);

export const getBrandProviderStatuses = (
  brandId: string | number,
  config?: import('axios').AxiosRequestConfig
) =>
  api.get<ResponseType<ProviderAndProviderStatusResponse>>(
    `/v1/brands/${brandId}/providers-statuses`,
    config
  );

export const getBrandConnectionStatus = (brandId: string | number) =>
  api.get<ResponseType<ProviderAndConnectionStatusResponse>>(
    `/v1/brands/${brandId}/provider-connection-statuses`
  );

/** `confirmUnpaidOrders` only after the user has seen the orders in the
 *  409 and agreed to pay them out from the subtotal - never up front. */
export const disconnectBrandProvider = (
  brandId: string | number,
  provider: Provider,
  confirmUnpaidOrders?: boolean
) =>
  api.post<ResponseType<null>>(`/v1/brands/${brandId}/provider/disconnect`, {
    provider,
    ...(confirmUnpaidOrders && { confirmUnpaidOrders: true }),
  });

export const getProviderStatus = (
  brandId: string | number,
  provider: Provider
) =>
  api.get<ResponseType<{ paused: boolean }>>(
    `/v1/brands/${brandId}/provider/status`,
    {
      params: {
        provider,
      },
    }
  );

export const pauseBrandProvider = (
  brandId: string | number,
  data: { provider: Provider } & ProviderPauseLength
) => api.post<ResponseType<null>>(`/v1/brands/${brandId}/provider/pause`, data);

export const unpauseBrandProvider = (
  brandId: string | number,
  provider: Provider
) =>
  api.post<ResponseType<null>>(`/v1/brands/${brandId}/provider/unpause`, {
    provider,
  });

export const getBrandById = (brandId: string | number) =>
  api.get<ResponseType<Brand>>(`/v1/brands/${brandId}`);

export const updateBrandById = (brandId: string | number, data: FormData) =>
  api.put<ResponseType<Brand>>(`/v1/brands/${brandId}`, data, {
    headers: { 'content-type': 'multipart/form-data' },
  });

/**
 * The brand's Google My Business review link, offered on the tracking page
 * after a 4-5 star rating - only for a virtual brand: a location's own brand
 * uses the location's link. `null` clears it; anything else must start with
 * http(s):// or it is a 400.
 */
export const updateBrandGoogleBusinessUrl = (
  brandId: string | number,
  googleBusinessUrl: string | null
) =>
  api.put<ResponseType<Brand>>(`/v1/brands/${brandId}/google-business-url`, {
    googleBusinessUrl,
  });

export const updateBrandNotes = (brandId: string | number, notes: string) =>
  api.put<ResponseType<Brand>>(`/v1/brands/${brandId}/notes`, {
    notes,
  });

export const getBrandFooterPrintingMessage = (brandId: string | number) =>
  api.get<ResponseType<FooterMessageResponse>>(
    `/v1/brands/${brandId}/footer-message`
  );

export const updateBrandFooterPrintingMessage = (
  brandId: string | number,
  message: string | null
) =>
  api.put<ResponseType<null>>(`/v1/brands/${brandId}/footer-message`, {
    message,
  });

export const deleteBrandFooterImage = (brandId: string | number) =>
  api.delete<ResponseType<null>>(`/v1/brands/${brandId}/footer-image`);

export const updateBrandPrintingSettings = (
  brandId: string | number,
  data: FormData
) =>
  api.put<ResponseType<null>>(
    `/v1/brands/${brandId}/printing-settings`,
    data,
    MULTIPART_HEADERS
  );

export const getAllProvidersPaused = (brandId: string | number) =>
  api.get<ResponseType<{ paused: boolean }>>(
    `/v1/brands/${brandId}/provider/paused-all`
  );

export const pauseAllBrandProviders = (
  brandId: string | number,
  length: ProviderPauseLength
) =>
  api.post<ResponseType<null>>(
    `/v1/brands/${brandId}/provider/pause-all`,
    length
  );

export const unpauseAllBrandProviders = (brandId: string | number) =>
  api.post<ResponseType<null>>(`/v1/brands/${brandId}/provider/unpause-all`);

export const getBrandsHolidayHours = (brandId: string | number) =>
  api.get<ResponseType<Array<HolidayHoursReponseV2>>>(
    `/v2/brands/${brandId}/holiday-hours`
  );

export const updateBrandsHolidayHours = (
  brandId: string | number,
  data: UpdateHolidayHoursLocationV2Dto
) =>
  api.post<ResponseType<Array<HolidayHoursReponseV2>>>(
    `/v2/brands/${brandId}/holiday-hours`,
    data
  );

export const deleteHolidayHours = (
  brandId: string | number,
  data: {
    date: string;
    provider: Provider;
  }
) =>
  api.delete<ResponseType<null>>(`/v1/brands/${brandId}/holiday-hours`, {
    data,
  });

export const getBrandsWorkingHours = (brandId: string | number) =>
  api.get<ResponseType<WorkingHoursReponseV2>>(
    `/v2/brands/${brandId}/working-hours`
  );

export const updateBrandsWorkingHours = (
  brandId: string | number,
  data: UpdateWorkingHoursLocationV2Dto
) =>
  api.post<ResponseType<WorkingHoursReponseV2>>(
    `/v2/brands/${brandId}/working-hours`,
    data
  );

export const createBrandsWorkingHours = (
  brandId: string | number,
  data: CreateWorkingHoursRequestData
) => api.post<ResponseType<null>>(`/v1/brands/${brandId}/working-hours`, data);

export const deleteBrandsWorkingHours = (
  brandId: string | number,
  data: {
    provider: Provider;
    id: string;
  }
) =>
  api.delete<ResponseType<null>>(`/v1/brands/${brandId}/working-hours`, {
    data,
  });

export const updateBrandSlipFromSheet = (name: string) =>
  api.post<ResponseType<null>>(`/v1/brands/brand-from-sheet`, { name });

export const deleteBrandById = (
  id: string | number,
  confirmUnpaidOrders?: boolean
) =>
  api.post<ResponseType<null>>(
    `/v1/brands/${id}/delete`,
    confirmUnpaidOrders ? { confirmUnpaidOrders: true } : undefined
  );

export const getBrandsPausedTimes = (brandId: string | number) =>
  api.get<ResponseType<Array<BrandPausedTimeEntity>>>(
    `/v1/brands/${brandId}/paused-times`
  );

export const addBrandPausedTime = (
  brandId: string | number,
  data: BrandPausedTimeDto
) =>
  api.post<ResponseType<BrandPausedTimeEntity>>(
    `/v1/brands/${brandId}/paused-times`,
    data
  );

export const deleteBrandPausedTime = (
  brandId: string | number,
  pausedTimeId: number | string
) =>
  api.delete<ResponseType<null>>(
    `/v1/brands/${brandId}/paused-times/${pausedTimeId}`
  );

export const getBrandsOverview = (
  id: string | number,
  params: GetLocationsOverviewParams
) =>
  api.get<ResponseType<OrdersOverviewEntity>>(
    `/v2/brands/${id}/orders/overview`,
    {
      params,
    }
  );

export const getAllUnverifiedBrands = (
  params: PaginationParams<{ query?: string }>
) =>
  api.get<ResponseType<Array<BrandRequestEntity>>>('/v1/brands/unverified', {
    params,
  });

export const getBrandRequestDetails = (brandId: string | number) =>
  api.get<ResponseType<BrandRequestDetailsEntity>>(
    `/v1/brands/${brandId}/request`
  );

export const approveBrandRequest = (brandId: string | number) =>
  api.post<ResponseType<null>>(`/v1/brands/${brandId}/request/approve`);

export const rejectBrandRequest = (brandId: string | number, reason: string) =>
  api.post<ResponseType<null>>(`/v1/brands/${brandId}/request/reject`, {
    reason,
  });

export const brandRequestUpdateItem = (
  brandId: string | number,
  data: FormData
) =>
  api.post<ResponseType<BrandRequestDetailsEntity>>(
    `/v1/brands/${brandId}/request/update-item`,
    data,
    MULTIPART_HEADERS
  );

export const activatingBrandRequest = (
  brandId: string | number,
  activating: boolean
) =>
  api.post<ResponseType<null>>(`/v1/brands/${brandId}/request/activating`, {
    activating,
  });

export const getBrandMenuPdf = (brandId: string | number) =>
  api.get<ResponseType<string>>(`/v1/brands/${brandId}/request/menu-pdf`);

export const getBrandMenuCsv = (brandId: string | number) =>
  api.get<ResponseType<string>>(`/v1/brands/${brandId}/request/menu-csv`);

export const getPartnerBrandTemplatesV2 = (
  params: PaginationParams<{
    query?: string | null;
    locationId: number;
    foodTypes?: FoodType[] | null;
  }>
) =>
  api.get<ResponseType<BrandTemplateEntity[]>>(`/v2/brands/templates`, {
    params,
  });

export const getPartnerBrandTemplateMenu = (brandTemplateId: string | number) =>
  api.get<ResponseType<BTMenuResponse>>(
    `/v2/brands/templates/${brandTemplateId}/menu`
  );

export const initiateBrandRequest = (data: InitiateBrandRequestDto) =>
  api.post<ResponseType<null>>(`/v2/brands/templates`, data);
